/**
 * Shared apply path: Manual fixed overrides → buildEmployeeStructureBreakdown → save payload.
 * CTC/Gross on the employee profile is the source of truth (never overwritten here).
 */
import { getAuthJson } from "./authStorage";
import { buildEmployeeStructureBreakdown } from "./employeeStructureBreakdown";
import {
  normalizeSalaryStructureMethod,
  resolveStructureMode,
  matchProfilePackageAmount,
} from "./salaryStructureMethod";
import { resolvePtStateFromEmployee } from "../components/Admin/employee-management/salary-structure/utils/ptStates";
import { isBasicLike } from "../components/Admin/employee-management/salary-structure/utils/salaryStructureSimulatorCompute";
import { salaryStructureService } from "../services/api";

/**
 * Normalize any date-like value to YYYY-MM-DD (calendar date, no TZ shift tricks).
 */
export function toStructureDateOnly(value) {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    const s = value.trim();
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
    const dmy = s.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
    if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const s = String(value).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const dmy = s.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  const parsed = new Date(s);
  if (!Number.isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, "0");
    const d = String(parsed.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  return null;
}

/** First calendar day of the month for a date-like value → YYYY-MM-01 */
export function toStructureMonthStart(value) {
  const day = toStructureDateOnly(value);
  if (!day) return null;
  return `${day.slice(0, 7)}-01`;
}

/**
 * Resolve effectiveFrom for employee salary structure saves.
 * - explicitEffectiveFrom: salary revision / Excel import (always win)
 * - activeEffectiveFrom: profile re-save keeps existing date (do not stamp "today")
 * - joiningDate: first structure → exact DOJ (not month-start)
 * - last resort: current calendar date (date-only; never invent month-start of "now")
 */
export function resolveEmployeeStructureEffectiveFrom({
  explicitEffectiveFrom = null,
  activeEffectiveFrom = null,
  joiningDate = null,
  now = new Date(),
} = {}) {
  const explicit = toStructureDateOnly(explicitEffectiveFrom);
  if (explicit) return explicit;

  const active = toStructureDateOnly(activeEffectiveFrom);
  if (active) return active;

  const fromDoj = toStructureDateOnly(joiningDate);
  if (fromDoj) return fromDoj;

  return toStructureDateOnly(now);
}

export function employeeJoiningDateForStructure(employee = {}) {
  return (
    employee.joiningDate ||
    employee.dateOfJoining ||
    employee.doj ||
    employee.date_of_joining ||
    employee.joining_date ||
    null
  );
}

export function isManualFixedComponent(component) {
  if (!component) return false;
  if (component.autoAdjustCTC) return false;
  if (component.isActive === false || component.active === false) return false;
  const calcType = String(component.calculationType || "").toLowerCase();
  if (calcType === "formula") return false;
  if (calcType && calcType !== "manual") return false;
  const method = String(component.calculationMethod || "fixed")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  if (method === "monthlyvariable" || method === "onetime") return false;
  const basis = String(component.calculationBasis || "").toLowerCase();
  if (basis === "recurrent" || basis === "recurring") return false;
  return true;
}

export function listManualFixedComponents(structure) {
  const components = Array.isArray(structure?.components) ? structure.components : [];
  return components.filter(isManualFixedComponent);
}

/** True when the structure expects employee-level Manual ₹ amounts (not CTC-only Auto). */
export function structureRequiresManualInput(structure) {
  return listManualFixedComponents(structure).length > 0;
}

export function structureCalcModeLabel(structure) {
  return structureRequiresManualInput(structure) ? "Manual" : "Auto (Formula)";
}

export function manualFixedColumnLabel(component) {
  const name = String(component?.name || component?.code || "Component").trim();
  return name;
}

/**
 * Look up a Manual monthly override by id / name / code.
 * Returns undefined when the key is absent (UI may still show breakdown monthly).
 * Explicit 0 is a valid entered amount.
 */
export function lookupManualOverride(comp, overrides = {}) {
  if (!comp || !overrides || typeof overrides !== "object") return undefined;
  const id = comp.id != null ? String(comp.id) : "";
  const name = String(comp.name || "").trim();
  const code = String(comp.code || "").trim();
  if (id && Object.prototype.hasOwnProperty.call(overrides, id)) {
    return overrides[id];
  }
  if (name && Object.prototype.hasOwnProperty.call(overrides, name)) {
    return overrides[name];
  }
  if (code && Object.prototype.hasOwnProperty.call(overrides, code)) {
    return overrides[code];
  }
  return undefined;
}

/** Parse Manual monthly ₹ — blank is missing; 0 is allowed; negatives invalid. */
export function parseManualMonthlyAmount(raw) {
  if (raw == null || raw === "") {
    return { ok: false, missing: true, value: null };
  }
  const n = Number(String(raw).replace(/,/g, "").trim());
  if (!Number.isFinite(n)) {
    return { ok: false, invalid: true, value: null };
  }
  if (n < 0) {
    return { ok: false, invalid: true, value: null };
  }
  return { ok: true, value: n };
}

/**
 * Effective Manual amount for save/validation — same fallback chain as the UI input:
 * override → breakdown monthly → template manualAmount.
 */
export function resolveManualMonthlyForSave(comp, overrides = {}, fallbackMonthly) {
  const fromOverride = lookupManualOverride(comp, overrides);
  if (fromOverride !== undefined && fromOverride !== "") {
    return parseManualMonthlyAmount(fromOverride);
  }
  if (fallbackMonthly != null && fallbackMonthly !== "") {
    const fromFallback = parseManualMonthlyAmount(fallbackMonthly);
    if (fromFallback.ok) return fromFallback;
  }
  if (comp?.manualAmount != null && comp.manualAmount !== "") {
    return parseManualMonthlyAmount(comp.manualAmount);
  }
  if (fromOverride === "" || fromOverride === undefined) {
    return { ok: false, missing: true, value: null };
  }
  return parseManualMonthlyAmount(fromOverride);
}

/**
 * Build a complete overrides map for every Manual fixed component.
 * @returns {{ ok: true, overrides: object } | { ok: false, missing: object[] }}
 */
export function buildCompleteManualOverrides(
  manualComponents,
  overrides = {},
  fallbackByName = {}
) {
  const next = {};
  const missing = [];
  (manualComponents || []).forEach((comp) => {
    const name = String(comp?.name || "").trim();
    const fallback =
      (name && fallbackByName[name] != null ? fallbackByName[name] : null) ??
      (comp?.id != null && fallbackByName[String(comp.id)] != null
        ? fallbackByName[String(comp.id)]
        : null);
    const resolved = resolveManualMonthlyForSave(comp, overrides, fallback);
    if (!resolved.ok) {
      missing.push(comp);
      return;
    }
    if (comp.id != null && comp.id !== "") next[String(comp.id)] = resolved.value;
    if (name) next[name] = resolved.value;
    const code = String(comp?.code || "").trim();
    if (code) next[code] = resolved.value;
  });
  if (missing.length) return { ok: false, missing };
  return { ok: true, overrides: next };
}

function resolvedLineMonthlyAmount(row) {
  if (!row || typeof row !== "object") return null;
  const candidates = [
    row.monthly,
    row.amount,
    row.monthlyAmount,
    row.value,
    row.resolvedAmount,
  ];
  for (const c of candidates) {
    if (c == null || c === "") continue;
    const n = Number(String(c).replace(/,/g, ""));
    if (Number.isFinite(n)) return n;
  }
  const annual = Number(row.annual ?? row.annualAmount);
  if (Number.isFinite(annual)) return Math.round((annual / 12) * 100) / 100;
  return null;
}

/**
 * Rebuild Manual overrides from a saved employee snapshot (parametersJson or resolved lines).
 */
export function extractManualOverridesFromSnapshot(activeStructure, templateStructure) {
  const params =
    activeStructure?.parametersJson && typeof activeStructure.parametersJson === "object"
      ? activeStructure.parametersJson
      : typeof activeStructure?.parametersJson === "string"
        ? (() => {
            try {
              return JSON.parse(activeStructure.parametersJson);
            } catch {
              return {};
            }
          })()
        : {};
  const saved = params?.manualOverrides;
  if (saved && typeof saved === "object" && Object.keys(saved).length > 0) {
    return { ...saved };
  }

  const resolved = Array.isArray(activeStructure?.resolvedComponentsJson)
    ? activeStructure.resolvedComponentsJson
    : typeof activeStructure?.resolvedComponentsJson === "string"
      ? (() => {
          try {
            return JSON.parse(activeStructure.resolvedComponentsJson);
          } catch {
            return [];
          }
        })()
      : [];

  const overrides = {};
  for (const comp of listManualFixedComponents(templateStructure)) {
    const name = String(comp.name || "").trim();
    const code = String(comp.code || "").trim();
    const id = comp.id;
    const match = resolved.find((r) => {
      const rn = String(r?.name || r?.componentName || "").trim().toLowerCase();
      const rc = String(r?.code || "").trim().toLowerCase();
      return (
        (name && rn === name.toLowerCase()) ||
        (code && (rc === code.toLowerCase() || rn === code.toLowerCase()))
      );
    });
    if (!match) continue;
    const monthly = resolvedLineMonthlyAmount(match);
    if (monthly == null) continue;
    if (id != null && id !== "") overrides[String(id)] = monthly;
    if (name) overrides[name] = monthly;
    if (code) overrides[code] = monthly;
  }
  return overrides;
}

/**
 * Clone structure and apply monthly Manual fixed overrides keyed by component id or name.
 * Blank / missing override keeps template manualAmount.
 */
export function applyManualOverrides(structure, overrides = {}) {
  const byKey = new Map();
  Object.entries(overrides || {}).forEach(([key, value]) => {
    const k = String(key || "").trim().toLowerCase();
    if (!k) return;
    if (value == null) return;
    if (value === "") {
      byKey.set(k, 0);
      return;
    }
    const n = Number(String(value).replace(/,/g, "").trim());
    if (!Number.isFinite(n) || n < 0) return;
    byKey.set(k, n);
  });

  const components = (Array.isArray(structure?.components) ? structure.components : []).map(
    (c) => {
      if (!isManualFixedComponent(c)) return { ...c };
      const idKey = String(c.id ?? "").trim().toLowerCase();
      const nameKey = String(c.name || "").trim().toLowerCase();
      const codeKey = String(c.code || "").trim().toLowerCase();
      let amount;
      if (idKey && byKey.has(idKey)) amount = byKey.get(idKey);
      else if (nameKey && byKey.has(nameKey)) amount = byKey.get(nameKey);
      else if (codeKey && byKey.has(codeKey)) amount = byKey.get(codeKey);
      if (amount == null) return { ...c };
      return {
        ...c,
        manualAmount: amount,
        manualAmountType: "amount",
        calculationType: "manual",
        calculationMethod: "fixed",
      };
    }
  );

  return { ...structure, components };
}

/**
 * Employee-level override of Formula / earlier Balance CTC heads on an Auto structure.
 * `formulaOverrides` = { [componentId]: { amount, formulaAmount, reason, by, byName, at } }.
 * The final Balance CTC head (Special) must never be passed here — it absorbs the difference.
 */
export function applyFormulaOverrides(structure, formulaOverrides = {}) {
  const map = formulaOverrides && typeof formulaOverrides === "object" ? formulaOverrides : {};
  if (!structure || !Object.keys(map).length) return structure;
  const components = (Array.isArray(structure.components) ? structure.components : []).map(
    (c) => {
      const entry = map[String(c.id)];
      const amount = Number(entry?.amount);
      if (!entry || !Number.isFinite(amount) || amount < 0) return c;
      return {
        ...c,
        manualAmount: amount,
        manualAmountType: "amount",
        manualPercent: "",
        calculationType: "manual",
        calculationMethod: "fixed",
        selectedFormula: null,
        autoAdjustCTC: false,
        formulaOverride: true,
      };
    }
  );
  return { ...structure, components };
}

/** Minimum-wage floor for Basic — skipped when Basic is a Manual head or overridden for this employee. */
export function minimumWageForStructure(structure, minimumWageAmount) {
  if (minimumWageAmount == null || !Number.isFinite(Number(minimumWageAmount))) return null;
  const basic = (structure?.components || []).find((c) => isBasicLike(c));
  if (basic && (basic.formulaOverride || isManualFixedComponent(basic))) return null;
  return Number(minimumWageAmount);
}

function resolveAnnualAmount(employee) {
  return Number(employee?.ctc || employee?.ctcAmount || 0);
}

function resolveMode(structure, orgSalaryMethod) {
  // Structure template type drives Gross vs CTC match (source of truth for this breakdown).
  // Org method only used when structure type is missing.
  const structurePreferred =
    structure?.salaryType === "gross" || structure?.salaryType === "GROSS"
      ? "GROSS"
      : structure?.salaryType === "ctc" || structure?.salaryType === "CTC"
        ? "CTC"
        : null;
  if (structurePreferred) return structurePreferred;
  
  const orgMethod = normalizeSalaryStructureMethod(
    orgSalaryMethod || getAuthJson("companyData")?.defaultSalaryStructureMethod
  );
  return resolveStructureMode(orgMethod, "CTC");
}

/**
 * Build breakdown + save payload. Does not call the API.
 * @returns {{ ok: boolean, reason?: string, message?: string, breakdown?: object, payload?: object, structure?: object }}
 */
export function buildEmployeeStructureSavePayload({
  structure,
  employee,
  overrides = {},
  orgSalaryMethod,
  statutoryConfigJson = null,
  branchesList = [],
  minimumWageAmount = null,
  effectiveFrom = null,
  changeNote = null,
  formulaOverrides = null,
} = {}) {
  if (!structure?.id && structure?.id !== 0) {
    if (!structure) return { ok: false, reason: "missing_structure" };
  }
  if (!employee?.id) {
    return { ok: false, reason: "missing_employee" };
  }

  const annualAmount = resolveAnnualAmount(employee);
  if (!annualAmount || annualAmount <= 0) {
    return {
      ok: false,
      reason: "missing_ctc",
      message: "Employee profile amount (CTC / Gross) is required and must be > 0",
    };
  }

  const mode = resolveMode(structure, orgSalaryMethod);
  const withOverrides = applyFormulaOverrides(
    applyManualOverrides(structure, overrides),
    formulaOverrides
  );
  const salaryType = mode === "GROSS" ? "gross" : "ctc";

  const breakdown = buildEmployeeStructureBreakdown({
    structure: withOverrides,
    annualAmount,
    salaryType,
    employee,
    minimumWageAmount,
    statutoryConfigJson,
    branchesList,
    ptState: resolvePtStateFromEmployee(employee, branchesList),
  });

  if (!breakdown) {
    return { ok: false, reason: "breakdown_failed", structure: withOverrides };
  }

  if (breakdown.complianceError || breakdown.canSave === false) {
    return {
      ok: false,
      reason: "compliance",
      message:
        breakdown.complianceError ||
        "Cannot save — profile amount does not fit this structure.",
      breakdown,
      structure: withOverrides,
    };
  }

  const hasBalancer = (withOverrides.components || []).some((c) => !!c.autoAdjustCTC);
  const monthlyProfile = Number(breakdown.totals?.monthlyCtc || breakdown.monthlyCtc || 0);
  const monthlySubtotalE =
    Number(breakdown.totals?.earningsSubtotal ?? 0) ||
    Number(breakdown.totals?.earnings || 0) + Number(breakdown.totals?.benefits || 0) ||
    Number(breakdown.totals?.gross || 0);
  const monthlyActualCtc = Number(breakdown.totals?.actualCtcMonthly || 0);
  const match = matchProfilePackageAmount({
    mode,
    profileAmount: monthlyProfile,
    grossAmount: monthlySubtotalE,
    totalCtcAmount: monthlyActualCtc,
    hasReimbursements: Number(breakdown.totals?.reimbursements || 0) > 0,
    tolerance: 1,
  });
  // Auto / Balance CTC structures fit by design; Manual without balancer must match profile rule.
  if (!hasBalancer && !match.ok) {
    return {
      ok: false,
      reason: "ctc_mismatch",
      message: `${match.matchedLabel} (₹${match.matchedAmount.toLocaleString("en-IN")}/mo) does not match ${match.profileLabel} (₹${match.targetAmount.toLocaleString("en-IN")}/mo). Add a Balance CTC component or adjust Manual amounts.`,
      breakdown,
      structure: withOverrides,
    };
  }

  const effective =
    resolveEmployeeStructureEffectiveFrom({
      explicitEffectiveFrom: effectiveFrom,
      joiningDate: employeeJoiningDateForStructure(employee),
    });

  const payload = {
    employeeId: employee.id,
    templateId: structure.id,
    targetAnnualCtc: annualAmount,
    mode,
    cityType: structure.defaultCityType || "METRO",
    parametersJson: {
      structureId: structure.id,
      structureName: structure.name,
      statutoryApplicability: breakdown.applicability,
      manualOverrides: overrides || {},
      ...(formulaOverrides && Object.keys(formulaOverrides).length ? { formulaOverrides } : {}),
    },
    resolvedComponentsJson: [
      ...breakdown.earningsList,
      ...breakdown.benefitsList,
      ...breakdown.reimbursementsList,
      ...breakdown.deductionsList,
      ...(breakdown.statutoryDeductionsList || []).map((r) => ({
        ...r,
        category: "DEDUCTION",
        type: "Deduction",
      })),
      ...(breakdown.statutoryEmployerList || []).map((r) => ({
        ...r,
        category: "EMPLOYER_BENEFIT",
        type: "Employer",
      })),
    ],
    computedSummaryJson: {
      ...breakdown.totals,
      monthlyCtc: breakdown.monthlyCtc,
      annualAmount,
      structureName: structure.name,
      statutoryDeductionsList: breakdown.statutoryDeductionsList || [],
      statutoryEmployerList: breakdown.statutoryEmployerList || [],
    },
    complianceReportJson: [],
    effectiveFrom: effective,
    status: "active",
    changeNote:
      changeNote ||
      `Applied structure with Manual component values: ${structure.name}`,
  };

  return {
    ok: true,
    breakdown,
    payload,
    structure: withOverrides,
  };
}

/**
 * Compute + persist employee structure (same path as onboard / designer).
 */
export async function applyAndSaveEmployeeStructure(options = {}) {
  const built = buildEmployeeStructureSavePayload(options);
  if (!built.ok) return built;
  await salaryStructureService.saveEmployeeStructure(built.payload);
  return { ...built, saved: true };
}
